import "./App.css";
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, HashRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";
import { PageViewTracker } from "./components/PageViewTracker";
import { CookieConsentBanner } from "./components/CookieConsentBanner";
import LandingPage from "./pages/LandingPage";

// The desktop app bundles this build and opens it from disk (file://), where BrowserRouter's
// history API doesn't work. HashRouter needs no server support, so it works both offline and
// on disk. The hosted website is served over http(s), so it keeps using BrowserRouter as before.
const Router = window.location.protocol === "file:" ? HashRouter : BrowserRouter;

function OfflineBanner() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);
  if (online) return null;
  return (
    <div
      className="fixed top-0 inset-x-0 z-[100] bg-red-600 text-white text-center text-xs sm:text-sm py-1.5 font-medium"
      data-testid="offline-banner"
    >
      You're offline — live features will resume automatically once your connection is back.
    </div>
  );
}

const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const EditorPage = lazy(() => import("./pages/EditorPage"));
const BrandingPage = lazy(() => import("./pages/BrandingPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const WebsiteAnalyticsPage = lazy(() => import("./pages/WebsiteAnalyticsPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AcceptInvitePage = lazy(() => import("./pages/AcceptInvitePage"));
const PresentPage = lazy(() => import("./pages/PresentPage"));
const RemotePage = lazy(() => import("./pages/RemotePage"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const TemplatesPage = lazy(() => import("./pages/TemplatesPage"));
const PresentationAnalyticsPage = lazy(() => import("./pages/PresentationAnalyticsPage"));
const JoinPage = lazy(() => import("./pages/JoinPage"));
const PlayPage = lazy(() => import("./pages/PlayPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const StepByStepGuidePage = lazy(() => import("./pages/StepByStepGuidePage"));
const DashboardGuidePage = lazy(() => import("./pages/DashboardGuidePage"));
const DashboardContactPage = lazy(() => import("./pages/DashboardContactPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));

const DARK_ROUTE_PREFIXES = ["/login", "/join", "/play", "/present", "/remote"];

function PageFallback() {
  const { pathname } = useLocation();
  const isDark = DARK_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  return (
    <div
      className={`min-h-screen flex items-center justify-center ${isDark ? "bg-[#0B0F19]" : "bg-background"}`}
      data-testid="route-loading-fallback"
    >
      <div className={`w-8 h-8 rounded-full border-2 ${isDark ? "border-white/15" : "border-foreground/15"} border-t-[#4F46E5] animate-spin`} />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <OfflineBanner />
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <PageViewTracker />
            <CookieConsentBanner />
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/join" element={<JoinPage />} />
                <Route path="/join/:code" element={<JoinPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/guide" element={<StepByStepGuidePage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route
                  path="/dashboard/guide"
                  element={
                    <ProtectedRoute>
                      <DashboardGuidePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/contact"
                  element={
                    <ProtectedRoute>
                      <DashboardContactPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/play/:code" element={<PlayPage />} />
                <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/team"
                  element={
                    <ProtectedRoute>
                      <TeamPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/editor/:id"
                  element={
                    <ProtectedRoute>
                      <EditorPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/branding"
                  element={
                    <ProtectedRoute>
                      <BrandingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/analytics"
                  element={
                    <ProtectedRoute roles={["admin", "super_admin"]}>
                      <AnalyticsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/present/:id"
                  element={
                    <ProtectedRoute>
                      <PresentPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/remote/:id"
                  element={
                    <ProtectedRoute>
                      <RemotePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/help"
                  element={
                    <ProtectedRoute>
                      <HelpPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/templates"
                  element={
                    <ProtectedRoute>
                      <TemplatesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/presentations/:id/analytics"
                  element={
                    <ProtectedRoute>
                      <PresentationAnalyticsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute role="super_admin">
                      <AdminPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/website-analytics"
                  element={
                    <ProtectedRoute role="super_admin">
                      <WebsiteAnalyticsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/messages"
                  element={
                    <ProtectedRoute role="super_admin">
                      <MessagesPage />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Suspense>
          </Router>
          <Toaster />
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
